import { TableColumnsType } from "antd";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { BasePagination } from "api/interfaces/commonInterface.interface";
import {
  GetReportHealthList,
  RecorderHealthRow,
  ReportHealthList,
} from "api/interfaces/reportInterface.interface";
import { getHealthMergedReportList } from "api/reportAPI";
import TableButton from "components/atoms/buttons/TableButton";
import InfiniteExpandTable from "components/blocks/table/InfiniteExpandTable";
import useApiError from "hook/useApiError";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useAppSelector } from "redux/hooks";
import { baseQueryInfo } from "utils/queryUtil";

export default function HealthExpandContainer({
  mergedSystemId,
  queryKey,
  columns,
  onRowClick,
  reportId,
}: {
  mergedSystemId: string;
  queryKey: BasePagination;
  columns: TableColumnsType<RecorderHealthRow>;
  onRowClick: (row: RecorderHealthRow) => void;
  reportId: string;
}) {
  const { lang, notifyError } = useApiError();
  const [mergedSystemQueryInfo, setMergedSystemQueryInfo] =
    useState<BasePagination>({
      ...baseQueryInfo,
      sortDirection: queryKey.sortDirection,
    });
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const onInitialData = () => {
    setMergedSystemQueryInfo({
      ...queryKey,
    });
  };
  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(
    [
      "healthMergedList",
      mergedSystemId,
      mergedSystemQueryInfo,
      reportId,
      selectedAccount.accountId,
    ],

    ({ pageParam = 0 }) =>
      getHealthMergedReportList({
        payload: {
          ...mergedSystemQueryInfo,
          pageNum: pageParam,
        },
        reportId: reportId as string,
        mergedSystemId: mergedSystemId as string,
        accountId: selectedAccount.accountId,
      }),
    {
      getNextPageParam: (lastPage: GetReportHealthList | undefined) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 0,
      onError: (e: any) => {
        onInitialData();
        notifyError(lang.label.report.notify.health.list.error);
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["reportDetailUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const mergedColumns = useMemo(() => {
    return [
      ...columns.map((column) => {
        if (column.key === "type" || column.title === "Access") {
          return {
            ...column,
            render: () => "",
          };
        }
        if (column.key === "view") {
          return {
            ...column,
            render: (row: RecorderHealthRow) => (
              <span className="row-click-button table-button-wrapper">
                <TableButton
                  label="View"
                  onClick={() => onRowClick(row)}
                  // disabled={
                  //   row.systemCount + row.diskCount + row.videoCount === 0
                  //     ? true
                  //     : false
                  // }
                  data-testid="reports-view-health-table-button-row"
                />
              </span>
            ),
          };
        }
        return column;
      }),
    ];
  }, [columns]);

  const list = useMemo(() => {
    return (data?.pages || [])
      .flatMap((page: GetReportHealthList) => page.result?.healthReports ?? [])
      .map((item: ReportHealthList, index: number) => {
        return {
          ...item,
          title: item.recorderName,
          version: item.firmwareVersion,
          status: "status",
          totalStorage: item.storages.reduce(function (prev: any, next: any) {
            return prev + next.total;
          }, 0),
          freeStorage: item.storages.reduce(function (prev: any, next: any) {
            return prev + next.free;
          }, 0),
          totalCamera: item.cameraCount,
          lossCamera: item.videoLoss,
          key: index,
          cloudSystemId: "",
        };
      });
  }, [data]);

  useEffect(() => {
    setMergedSystemQueryInfo((info) => ({
      ...info,
      sortDirection: queryKey.sortDirection,
      sortType: queryKey.sortType,
      pageNum: 0,
    }));
  }, [queryKey.sortDirection, queryKey.sortType]);
  return (
    <InfiniteExpandTable
      data={list}
      loadMore={fetchNextPage}
      isLoading={isInitialLoading || isFetchingNextPage}
      hasMore={hasNextPage && !isFetchingNextPage && !isInitialLoading}
      columns={mergedColumns as TableColumnsType<RecorderHealthRow>}
      leftPadding={48}
    />
  );
}
